import { httpClient } from '@/api/http';

export interface MerchantCategory {
  id: number;
  name: string;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Merchant {
  id: number;
  name: string;
  description: string;
  industryCategory?: MerchantCategory;
  industryCategories?: MerchantCategory[];
  city: string;
  state?: string;
  address?: string | null;
  mapUrl?: string | null;
  earnRate: number;
  isFeatured: boolean;
  logo: string;
  banner: string;
  images?: string[];
  websiteUrl?: string;
  contactPerson: string;
  contactMobile: string;
  businessEmail?: string;
  partnershipRequestId?: number;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateMerchantPayload {
  name: string;
  description: string;
  industryCategoryId: number;
  industryCategoryIds: number[];
  city: string;
  state: string;
  address: string;
  mapUrl: string;
  earnRate: number;
  isFeatured: boolean;
  logo: string;
  banner: string;
  images: string[];
  websiteUrl: string;
  contactPerson: string;
  contactMobile: string;
  businessEmail: string;
  partnershipRequestId?: number;
}

export interface GetMerchantsParams {
  skip?: number;
  take?: number;
  search?: string;
  city?: string;
  state?: string;
  industryCategory?: string;
  isFeatured?: boolean;
  status?: string;
}

export interface GetMerchantsResponse {
  count: number;
  skip: number;
  take: number;
  data: Merchant[];
}

export async function getMerchantCategories(): Promise<MerchantCategory[]> {
  const { data } = await httpClient.get<any>('/merchants/categories');
  return Array.isArray(data) ? data : data?.data || [];
}

export async function createMerchantCategory(payload: { name: string }): Promise<MerchantCategory> {
  const { data } = await httpClient.post<MerchantCategory>('/merchants/categories', payload);
  return data;
}

export async function getMerchants(params: GetMerchantsParams): Promise<GetMerchantsResponse> {
  const { data } = await httpClient.get<GetMerchantsResponse>('/merchants/all', { params });
  return data;
}

export async function createMerchant(payload: CreateMerchantPayload): Promise<Merchant> {
  const { data } = await httpClient.post<Merchant>('/merchants', payload);
  return data;
}

export async function updateMerchant(
  merchantId: number,
  payload: CreateMerchantPayload
): Promise<Merchant> {
  const { data } = await httpClient.patch<Merchant>(`/merchants/${merchantId}`, payload);
  return data;
}

export type MerchantStatus = 'Active' | 'Inactive';

export async function updateMerchantStatus(
  merchantId: number,
  status: MerchantStatus
): Promise<void> {
  await httpClient.post(`/merchants/${merchantId}/${status}`);
}

export interface Coupon {
  id: number;
  title: string;
  description: string;
  discountLabel: string;
  image: string;
  pointsCost: number;
  totalCount: number;
  redeemedCount: number;
  validTill: string;
  status: string;
  merchantId: number;
  createdAt: string;
  updatedAt: string;
  remainingCount: number;
}

export interface MerchantDetail extends Merchant {
  address: string | null;
  mapUrl: string | null;
  openingHours: string | null;
  totalLocations: number;
  images: string[];
  coupons: Coupon[];
  offersCount: number;
}

export async function getMerchant(merchantId: number): Promise<MerchantDetail> {
  const { data } = await httpClient.get<MerchantDetail>(`/merchants/${merchantId}`);
  return data;
}

export interface GetAdminCategoriesParams {
  skip?: number;
  take?: number;
  search?: string;
  isActive?: boolean;
}

export interface GetAdminCategoriesResponse {
  count: number;
  skip: number;
  take: number;
  data: MerchantCategory[];
}

export async function getAdminCategories(params?: GetAdminCategoriesParams): Promise<GetAdminCategoriesResponse> {
  const { data } = await httpClient.get<GetAdminCategoriesResponse>('/admin/categories', { params });
  return data;
}

export async function updateCategoryStatus(categoryId: number, isActive: boolean): Promise<void> {
  await httpClient.patch(`/admin/category/${categoryId}/status`, { isActive });
}

export async function updateCategoryName(categoryId: number, name: string): Promise<MerchantCategory> {
  const { data } = await httpClient.patch<MerchantCategory>(`/merchants/categories/${categoryId}`, { name });
  return data;
}

export async function deleteCategory(categoryId: number): Promise<void> {
  await httpClient.delete(`/merchants/categories/${categoryId}`);
}

export interface CouponSale {
  merchantId: number;
  merchantName: string;
  month: string;
  totalSalesAmount: number;
  redemptionCount: number;
}

export interface GetCouponSalesParams {
  skip?: number;
  take?: number;
  search?: string;
  merchantId?: number;
  industryCategoryId?: number;
  fromDate?: string;
  toDate?: string;
  sortBy?: 'merchantName' | 'sales' | 'amount';
  sortOrder?: 'asc' | 'desc';
}

export interface GetCouponSalesResponse {
  count: number;
  skip: number;
  take: number;
  data: CouponSale[];
}

export async function getCouponSales(params: GetCouponSalesParams): Promise<GetCouponSalesResponse> {
  const { data } = await httpClient.get<GetCouponSalesResponse>('/admin/merchants/coupon-sales', { params });
  return data;
}

export interface MonthlySalesParams {
  skip?: number;
  take?: number;
  month: string;
  year: number;
}

export interface MonthlySaleItem {
  amount: number;
  invoice: string;
  redeemedAt: string;
  redeemedMerchant: {
    name: string;
    city: string;
    state: string;
    industryCategory?: {
      id: number;
      name: string;
      isActive: boolean;
    };
  };
  coupon: {
    discountType: string;
    discountValue: number;
  };
}

export interface GetMonthlySalesResponse {
  count: number;
  skip: number;
  take: number;
  totalAmount?: number;
  totalSalesAmount?: number;
  data: MonthlySaleItem[];
}

export async function getMerchantMonthlySales(
  merchantId: number,
  params: MonthlySalesParams
): Promise<GetMonthlySalesResponse> {
  const { data } = await httpClient.get<GetMonthlySalesResponse>(
    `/admin/merchant/${merchantId}/monthly-sales`,
    { params }
  );
  return data;
}

export async function deleteMerchant(merchantId: number): Promise<void> {
  await httpClient.delete(`/admin/merchants/${merchantId}`);
}

export async function updateCouponStatus(
  couponId: number,
  status: 'Active' | 'Inactive'
): Promise<void> {
  await httpClient.post(`/coupons/${couponId}/${status}`);
}

export interface CommissionReportSummary {
  totalMerchants: number;
  totalRedemptions: number;
  totalGrossSales: number;
  totalUplineCommissionDistributed: number;
}

export interface CommissionReportItem {
  merchantId: number;
  merchantName: string;
  city: string;
  state: string;
  isCustomVoucherRedeemConfig: boolean;
  effectiveSharePercentage: number;
  layerSplits: Record<string, number>;
  totalRedemptions: number;
  grossSalesAmount: number;
  totalUplineCommissionDistributed: number;
}

export interface CommissionReportResponse {
  summary?: CommissionReportSummary;
  skip?: number;
  take?: number;
  count: number;
  data: CommissionReportItem[];
}

export async function getMerchantCommissionReport(
  merchantId: number
): Promise<CommissionReportResponse> {
  const { data } = await httpClient.get<CommissionReportResponse>('/admin/reports/commission', {
    params: { merchantId },
  });
  return data;
}



